import {
  Player,
  InjurySeverity,
  Position,
  PotentialGrade,
  PlayerClass
} from '../types/game';
import { clamp, randomInt } from './math/variance';
import { StateRules, TEXAS_RULES } from './stateRules';

/**
 * Handles in-game stamina depletion and fatigue recovery on the sideline.
 */
export function updateInGameStamina(player: Player, snapsPlayed: number, position: Position): void {
  let burnRate = 1.8;
  if (position === 'RB' || position === 'DE' || position === 'LB') burnRate = 2.8;
  else if (position === 'OT' || position === 'OG' || position === 'C' || position === 'DT') burnRate = 2.2;
  else if (position === 'QB' || position === 'CB' || position === 'S') burnRate = 1.2;

  player.condition.inGameStamina = clamp(
    player.condition.inGameStamina - snapsPlayed * burnRate,
    15,
    100
  );
}

/**
 * Restores in-game stamina when a player rests on the bench during possessions.
 */
export function restPlayerOnSideline(player: Player, seriesRested: number): void {
  const recovery = seriesRested * 8.5;
  const maxCap = 100 - player.condition.seasonWear * 0.45; // fatigue caps how far a player bounces back
  player.condition.inGameStamina = clamp(player.condition.inGameStamina + recovery, 0, maxCap);
}

/**
 * Checks for injuries on contact plays with fatigue multipliers.
 */
export function evaluateSnapInjury(player: Player): InjurySeverity {
  if (player.condition.injuryStatus !== 'HEALTHY') return player.condition.injuryStatus;

  const fatigueMultiplier = player.condition.inGameStamina < 50 ? 2.8 : 1.0;
  const roll = Math.random() * 1000;

  if (roll < 1.2 * fatigueMultiplier) {
    player.condition.injuryStatus = 'SEASON_ENDING';
    player.condition.injuryWeeksRemaining = 20;
    return 'SEASON_ENDING';
  }
  if (roll < 6.0 * fatigueMultiplier) {
    player.condition.injuryStatus = 'MODERATE';
    player.condition.injuryWeeksRemaining = randomInt(2, 4);
    return 'MODERATE';
  }
  if (roll < 22.0 * fatigueMultiplier) {
    player.condition.injuryStatus = 'DINGED';
    player.condition.injuryWeeksRemaining = 1;
    return 'DINGED';
  }

  return 'HEALTHY';
}

/**
 * Decrements injury recovery counters during Monday triage. Injuries from this week's game are skipped,
 * so injuryWeeksRemaining is the number of games a player misses. A suspension only counts down in weeks the
 * team played (`playedGame`): one handed out in the summer is served in the first games of the season.
 */
export function processWeeklyInjuryHealing(player: Player, week?: number, playedGame = true): void {
  if (week !== undefined && player.condition.injuredInWeek === week) return;
  if (player.condition.isSuspended && !playedGame) return;
  if (player.condition.injuryWeeksRemaining > 0) {
    player.condition.injuryWeeksRemaining -= 1;
    if (player.condition.injuryWeeksRemaining === 0) {
      player.condition.injuryStatus = 'HEALTHY';
      player.condition.isSuspended = false;
    }
  }
}

/**
 * Evaluates the 6-week academic report cards for "No Pass, No Play" eligibility.
 */
/** Ineligible, or close enough to the state's eligibility line to be in danger (Texas: under 2.3, the line is 2.0). */
export function isAcademicallyAtRisk(player: Player, rules: StateRules = TEXAS_RULES): boolean {
  return !player.academics.isEligible || player.academics.gpa < rules.academics.atRiskGpa;
}

export function evaluateAcademicReport(player: Player, support = 0, rules: StateRules = TEXAS_RULES): void {
  // support: AI programs keep struggling players in study hall; the coach books a fix (sim/grades.ts)
  // A report card covers six weeks of school: grades can move up to 0.4 either way
  let gpaDelta = (Math.random() * 0.8 - 0.4) + support;
  if (player.attributes.footballIQ > 75) gpaDelta += 0.1;

  const before = player.academics.gpa;
  player.academics.gpa = clamp(Number((player.academics.gpa + gpaDelta).toFixed(2)), 1.2, 4.0);
  // The trend the Grades screen shows: the last few report cards (older saves start from the GPA they had)
  const history = player.academics.gpaHistory?.length ? player.academics.gpaHistory : [before];
  player.academics.gpaHistory = [...history, player.academics.gpa].slice(-4);

  if (player.academics.gpa < rules.academics.minimumGpa) {
    player.academics.isEligible = false;
    player.academics.consecutiveFailingWeeks += 3;
  } else {
    player.academics.isEligible = true;
    player.academics.consecutiveFailingWeeks = 0;
  }
}

/**
 * Calculates off-season player progression and growth spurts.
 */
// Position skills the roster generator ties to overall rating; they grow with it
export const POSITION_KEY_SKILLS: Record<Position, (keyof Player['attributes'])[]> = {
  QB: ['passingAccuracy', 'armStrength', 'vision'],
  RB: ['carrying', 'vision'],
  WR: ['routeRunning', 'catching'],
  TE: ['routeRunning', 'catching'],
  OT: ['runBlocking', 'passBlocking'],
  OG: ['runBlocking', 'passBlocking'],
  C: ['runBlocking', 'passBlocking'],
  DE: ['passRush', 'tackling'],
  DT: ['passRush', 'tackling'],
  LB: ['tackling'],
  CB: ['coverage'],
  S: ['coverage'],
  K: ['kickingPower', 'kickingAccuracy'],
  P: ['kickingPower', 'kickingAccuracy']
};

/** Offseason growth by potential (rating points a year), before breakouts and the strength coach. */
const POTENTIAL_GROWTH: Record<PotentialGrade, number> = { 'A+': 6, A: 4, B: 3, C: 1, D: 0 };
/** Breakout years by the class a player is leaving: the chance of a big jump and its range. */
const BREAKOUTS: Partial<Record<PlayerClass, { chance: number; min: number; max: number }>> = {
  Freshman: { chance: 0.3, min: 4, max: 8 },
  Sophomore: { chance: 0.2, min: 3, max: 6 },
  Junior: { chance: 0.1, min: 2, max: 4 }
};
const STRENGTH_COACH_BONUS_RATING = 80; // a strength coach this good adds a point a year

export function processOffSeasonProgression(player: Player, strengthCoachRating: number, trainedGrowth = 0): void {
  let growth = POTENTIAL_GROWTH[player.potential];

  const breakout = BREAKOUTS[player.classYear];
  if (breakout && Math.random() < breakout.chance) growth += randomInt(breakout.min, breakout.max);

  if (strengthCoachRating >= STRENGTH_COACH_BONUS_RATING) growth += 1;
  if (player.classYear === 'Senior') growth = Math.min(2, growth);

  // Weekly practice already delivered part of the year's skill growth (sim/training): leave a typical year's
  // share out of the overall and key skills (a fraction rounds up by chance)
  const cut = Math.floor(trainedGrowth) + (Math.random() < trainedGrowth - Math.floor(trainedGrowth) ? 1 : 0);
  const skillGrowth = Math.max(0, growth - cut);

  player.overallRating = clamp(player.overallRating + skillGrowth, 35, 99);
  player.attributes.strength = clamp(player.attributes.strength + growth, 35, 99);
  player.attributes.speed = clamp(player.attributes.speed + Math.floor(growth * 0.75), 35, 99);
  player.attributes.agility = clamp(player.attributes.agility + Math.floor(growth * 0.7), 35, 99);
  player.attributes.footballIQ = clamp(player.attributes.footballIQ + 3, 30, 99);
  POSITION_KEY_SKILLS[player.position].forEach((skill) => {
    player.attributes[skill] = clamp((player.attributes[skill] as number) + skillGrowth, 20, 99);
  });

  // Reset conditions for new year
  player.condition.seasonWear = 0;
  player.condition.inGameStamina = 100;
  player.condition.injuryStatus = 'HEALTHY';
  player.condition.injuryWeeksRemaining = 0;
}

/**
 * Emergency QB Fallback Selector if zero active quarterbacks remain.
 */
export function findEmergencyQuarterback(roster: Player[]): Player {
  const eligible = roster.filter((p) => p.academics.isEligible && p.condition.injuryStatus === 'HEALTHY');
  if (eligible.length === 0) return roster[0];

  return eligible.reduce((best, candidate) => {
    const candidateScore = candidate.attributes.armStrength * 0.4 + candidate.attributes.speed * 0.3 + candidate.attributes.footballIQ * 0.3;
    const bestScore = best.attributes.armStrength * 0.4 + best.attributes.speed * 0.3 + best.attributes.footballIQ * 0.3;
    return candidateScore > bestScore ? candidate : best;
  }, eligible[0]);
}
